import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fetchRestaurants } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useLocation as useSavedLocation } from "@/context/LocationContext";
import RestaurantCard from "@/components/RestaurantCard";

export default function Restaurants() {
  const { t, lang } = useLang();
  const { location } = useSavedLocation();
  const [params, setParams] = useSearchParams();
  const [all, setAll] = useState([]);
  const [categories, setCategories] = useState([]);
  const cuisine = params.get("cuisine") || "all";
  const q = params.get("q") || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchRestaurants(location.city_id);
        if (!cancelled) setAll(Array.isArray(r) ? r : []);
      } catch (e) { /* keep whatever we have */ }
      try {
        const c = await api.get("/categories");
        if (!cancelled) setCategories(Array.isArray(c.data) ? c.data : []);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [location.city_id]);

  const filtered = useMemo(() => {
    let list = all;
    if (cuisine !== "all") list = list.filter((r) => r.cuisine_en === cuisine);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((r) =>
        r.name_en.toLowerCase().includes(s) ||
        r.name_ar.includes(q) ||
        r.cuisine_en.toLowerCase().includes(s) ||
        r.menu.some((m) => m.name_en.toLowerCase().includes(s) || m.name_ar.includes(q))
      );
    }
    return list;
  }, [all, cuisine, q]);

  const setCuisine = (id) => {
    const np = new URLSearchParams(params);
    if (id === "all") np.delete("cuisine"); else np.set("cuisine", id);
    setParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-black text-[#1A1A1A]">{t("restaurants")}</h1>
      <p className="text-[#6B6B6B] mt-2">
        {filtered.length} {lang === "ar" ? `مطعم في ${location.city_ar}` : `restaurants in ${location.city_en}`}
      </p>

      <div className="mt-6 flex gap-2 flex-wrap" data-testid="cuisine-filter">
        <button
          onClick={() => setCuisine("all")}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all border ${cuisine === "all" ? "bg-[#E05D3A] text-white border-[#E05D3A]" : "bg-white text-[#1A1A1A] border-[#EAEAEA] hover:border-[#E05D3A]"}`}
          data-testid="cuisine-all"
        >
          {t("all")}
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setCuisine(c.id)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all border ${cuisine === c.id ? "bg-[#E05D3A] text-white border-[#E05D3A]" : "bg-white text-[#1A1A1A] border-[#EAEAEA] hover:border-[#E05D3A]"}`}
            data-testid={`cuisine-chip-${c.id}`}
          >
            <span className="me-1">{c.icon}</span>
            {lang === "ar" ? c.name_ar : c.name_en}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((r) => <RestaurantCard key={r.id} r={r} />)}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-[#6B6B6B]">
          {all.length === 0
            ? (lang === "ar" ? `لم نصل إلى ${location.city_ar} بعد — جايك متاح حالياً في مدن محددة` : `We haven't reached ${location.city_en} yet`)
            : (lang === "ar" ? "لا توجد نتائج" : "No results found")}
        </div>
      )}
    </div>
  );
}
